import React, { useContext, useEffect, useReducer, useState } from "react";
import { PatientSearchResult } from "../../services/medicationmanagement/patientApi/models/PatientSearchResult";
import { patientService } from "../../services/medicationmanagement/patientApi/PatientApi";
import { useAsync } from "../common/async";
import { ResidentFilter } from "./resident-filter/ResidentFilter";
import { ResidentTable} from "./resident-table/ResidentTable";
import { ResidentListHeader } from "./ResidentListHeader";
import { FilterStatus } from "./resident-filter/FilterStatus";
import { ResidentPagerContainer } from "./ResidentPagerContainer";
import { UserType } from "../../services/medicationmanagement/medsUser/MedsUserApi";
import {
  FilterActions,
  filterReducer,
  IResidentFilter,
  OrganizationAndFacilityType,
} from "./resident-filter/ResidentFilterReducer";
import { TwoPanelContainer } from "../common/two-panel-container/TwoPanelContainer";
import { ChartStatusFilter } from "../../services/medicationmanagement/patientApi/models/ChartStatusFilter";
import { IdentityContext } from "../auth/UserIdentity";
import { makeStyles } from "@material-ui/core/styles";
import Pagination from "@material-ui/lab/Pagination";
import SelectOrgSuccessDialogBox from "../common/dialogbox/SelectOrgSuccessDialogBox";
import SelectOrgErrorDialogBox, { 
} from "../common/dialogbox/SelectOrgErrorDialogBox";
import { QueryParams } from "../common/constants";
import { facilityService, Facilities } from "../../services/medicationmanagement/facilities/FacilitiesApi";
import { organizationService, Organization } from "../../services/medicationmanagement/organization/OrganizationApi";
import {ComponentLoader} from "../common";
import { CourseExpiryFilterSubType, ExpiryFilterType } from "../../services/medicationmanagement/patientApi/models/ExpiryFilterType";
import { logger } from "../common/logger";
import Footer from "../common/footer/Footer";
import { CommonApi } from "../../services/Common";

let medicationsUrl = (resident: PatientSearchResult, userType: UserType) => {
  if (userType == UserType.Doctor 
    || userType == UserType.Dentist 
    || userType == UserType.GeneralStaffAdmin 
    || userType == UserType.NursePractitioner 
    || userType == UserType.Optometrist 
    || userType == UserType.RegisteredNurse)
    return `/residents/${resident.authoringSystem}/${resident.authoringSystemForeignKey}`;
  else if (userType == UserType.Pharmacist)
    return `/pharmacy/${resident.authoringSystem}/${resident.authoringSystemForeignKey}`;
  else return "";
};

export const ResidentList = (props: any) => {
  const [lmsLink, setLmsLink] = useState(null);
  const [lmsApiError, setLmsApiError] = useState(false);

  useEffect(() => {
    const commonApi = new CommonApi();
    commonApi.getLmsLinks()
    .then(result => {
      setLmsLink(result);
      localStorage.setItem("lmsLink", JSON.stringify(result));
      localStorage.setItem("lmsApiError", 'false');
      props.onLmsLink(result);
      props.onLmsLinkReady(true);
    })
    .catch(error => {
      setLmsApiError(true);
      localStorage.setItem("lmsApiError", 'true');
      props.onLmsApiError(true);
      props.onLmsLinkReady(true);
    })
  },[]);

  let ctx = useContext(IdentityContext);
  if (!ctx?.userInfo?.userType) {
    return null;
  }
  return <UserResidentList userType={ctx.userInfo.userType} lmsLink={lmsLink} lmsApiError={lmsApiError} {...props} />;
};



const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      marginTop: theme.spacing(2),
    },
  },
}));

const getFacilities = (facilities:Facilities[],type:OrganizationAndFacilityType,id:string|null)=>{
  switch (type) {
    case OrganizationAndFacilityType.Facility:
      return id?[id]:[];
    case OrganizationAndFacilityType.Organization:
      return facilities.filter(a=>a.organisationId===id).map(x=>x.facilityId);
    default:
      return [];
  }
};

export const UserResidentList = (props: { userType: UserType , lmsLink:any, lmsApiError: boolean}) => {
  const [facility, setFacility] = useState<Facilities[]>([]);
  const [organization, setOrganization] = useState<Organization[]>([]);
  let residentFilter: IResidentFilter;
  //Get search criteria from local sotrage which stored previously to maintain search state if exists
  let searchStorage = localStorage.getItem('search');    
  if(searchStorage){
    residentFilter = JSON.parse(searchStorage);    
  }
  else{
    residentFilter = {
      key: 1,
      name: "",
      itemsPerPage: 25,
      selectedPage: 1,
      residentStatus: FilterStatus.Active,
      expiryFilterType: ExpiryFilterType.All,
      courseExpiryFilterSubType: CourseExpiryFilterSubType.CourseExpiryInSevenDays,
      chartStatus: props.userType == UserType.Pharmacist ?  ChartStatusFilter.WithChanges : undefined,
      facilities: { type: OrganizationAndFacilityType.All, id: null }    
    }
  }

  const [state, dispatch] = useReducer(       
    filterReducer,
    {
      key: residentFilter.key,
      name: residentFilter.name,
      itemsPerPage: residentFilter.itemsPerPage,
      selectedPage: residentFilter.selectedPage,
      residentStatus: residentFilter.residentStatus,
      expiryFilterType: residentFilter.expiryFilterType,
      courseExpiryFilterSubType: residentFilter.courseExpiryFilterSubType,
      chartStatus: residentFilter.chartStatus,
      facilities:{type:residentFilter.facilities.type,id:residentFilter.facilities.id}      
    },
    (data) => {
      switch (props.userType) {
        case UserType.Doctor:
        case UserType.Dentist:
        case UserType.GeneralStaffAdmin:
        case UserType.NursePractitioner:
        case UserType.Optometrist:
        case UserType.RegisteredNurse:
          return data;                                                           
        case UserType.Pharmacist:
          return {
            ...data       
          };
      }      
    }
  );

  let { inProgress, success, queryDispatcher,error } = useAsync();
  const classes = useStyles();

  function search(
    searchquery: string,
    pageNumber: number,
    pageLimit: number,
    residentStatus: string,
    expiryFilterType: ExpiryFilterType,
    courseExpiryFilterSubType: CourseExpiryFilterSubType,
    chartStatus?: ChartStatusFilter,
    facilities?: (string | undefined)[]
  ) {

    //Storing search criteria to a localstorage to maintain the search state while users navigate back from other pages. 
    const stateWithoutName = { ...state, name : "" };
    localStorage.setItem("search", JSON.stringify(stateWithoutName));
    queryDispatcher(
      patientService.search({
        limit: pageLimit,
        query: searchquery,
        page: pageNumber,
        residentStatus: residentStatus,
        expiryFilterType: expiryFilterType,
        courseExpiryFilterSubType: expiryFilterType == ExpiryFilterType.CourseDueNowOrSoon ? courseExpiryFilterSubType : undefined,
        chartStatus,
        facilities
      })
    );
  }

  const handlePageClick = (page?: number) => {
    if (state.selectedPage !== page) {
      dispatch([FilterActions.ChangeCurrentPage, page]);
    }
  };

  useEffect(() => {
    organizationService.getUserOrganization()
      .then(res => setOrganization(res));
    facilityService.getUserFacilities()
      .then(res => setFacility(res));
  }, []);

  useEffect(() => {
    let facilities = getFacilities(facility,state.facilities.type,state.facilities.id);
    search(
      state.name,
      state.selectedPage,
      state.itemsPerPage,
      FilterStatus[state.residentStatus],
      state.expiryFilterType,
      state.courseExpiryFilterSubType,
      state.chartStatus,
      facilities
    );
  }, [state]);


  let url = window.location.search;
  let params = new URLSearchParams(url);
  let isLinkSuccess = params.get(QueryParams.UserLinked);

  return (
    <React.Fragment>
    <TwoPanelContainer
      defaultState={"Open"}
      filters={
        <ResidentFilter
          filterState={state}
          filterData={success}
          userType={props.userType}
          facilityData={facility}
          organizationData={organization}
          onChange={dispatch}
        />
      }
      body={
        <>
          <SelectOrgSuccessDialogBox
            state={isLinkSuccess?.toLowerCase() == "true"}
          />
          
          <SelectOrgErrorDialogBox
            state={isLinkSuccess?.toLowerCase() == "false"}
          />
            <ComponentLoader 
                error={error} 
                inProgress={inProgress} 
                empty={success?.searchResults?.length<=0}
                emptyResultMessage={"No matching results found"}
            >
                <ResidentTable
                    loading={inProgress}
                    residents={success?.searchResults}
                    createUrl={medicationsUrl}
                    userType={props.userType}
                />
            </ComponentLoader>
          
          {success?.searchResults?.length > 0 ? (
            <ResidentPagerContainer>
              <div className={classes.root}>
                <Pagination
                  count={success.availablePages}
                  shape="rounded"
                  variant="outlined"
                  onChange={(key, val) => (handlePageClick(val))}
                  page={state.selectedPage}
                  boundaryCount={1}
                />
              </div>
            </ResidentPagerContainer>
          ) : null}
        </>
      }
      listHeaderTitle={
        <ResidentListHeader
          name={state.name}
          resultSize={success?.searchResults?.length}
          total={success?.total}
          residentStatus={state.residentStatus}
          expiryFilterType={state.expiryFilterType}
          chartStatus={state.chartStatus}
        />
      }
    />
    <Footer lmsLink={props.lmsLink} lmsApiError={props.lmsApiError}/>
    </React.Fragment>
  );
};
